import { Button, PetitionUpdateCard, Text } from '@change/ds-core-ui/next';

import { parseHtml } from '@change-corgi/core/react/html';
import { Translate, TranslatePlural } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { usePetitionUpdatesSection } from './hooks/usePetitionUpdatesSection';

export const PetitionUpdatesSection: React.FC = () => {
	const {
		data: { displayed, petitionUpdates, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = usePetitionUpdatesSection();

	const { containerRef, loadingRef, loadMoreRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	// TODO: uncomment this when we have the translations
	// const { translate } = useI18n();

	if (!displayed) {
		return null;
	}

	return (
		<div className="flex flex-col gap-6" data-qa="stories-and-updates-container">
			<Text
				as="h2"
				variant="heading"
				size="small"
				className="text-typography-strong mb-6"
				data-qa="stories-and-updates-heading"
			>
				<Translate value="corgi.local.petition_updates.heading" />
			</Text>

			<div ref={containerRef} className="flex flex-col items-center gap-6">
				<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
					{petitionUpdates.map((petitionUpdate, index) => (
						<div key={petitionUpdate.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<VisuallyHidden role="alert">
									<TranslatePlural
										value="corgi.tag.petitions.fetched_more"
										count={petitionUpdates.length - lastEvent.previousItemsCount}
										replacements={{ count: petitionUpdates.length - lastEvent.previousItemsCount }}
									/>
								</VisuallyHidden>
							)}

							<div className="h-full w-full" ref={getListItemRef(index)}>
								<PetitionUpdateCard
									data-qa="stories-and-updates-petition-card"
									className="h-full"
									description={parseHtml(petitionUpdate.description, { forbiddenTags: ['a'] })}
									href={`/p/${petitionUpdate.petition.slug}/u/${petitionUpdate.id}`}
									image={'photo' in petitionUpdate ? petitionUpdate.photo?.petitionMedium.url : null}
									imageAlt={'photo' in petitionUpdate ? petitionUpdate.title : undefined}
									metadata={petitionUpdate.createdAtLocalized}
									title={petitionUpdate.title}
									petitionTitle={petitionUpdate.petition.ask}

									// TODO: uncomment these when we have the translations
									// readMoreTriggerText={translate('corgi.local.petition_update_card.read_more')}
									// closeModalText={translate('corgi.local.petition_update_card.close_modal')}
								/>
							</div>
						</div>
					))}
				</div>

				{nextPageLoading && (
					<>
						<VisuallyHidden role="alert">
							<Translate value="corgi.tag.petitions.fetching_more" />
						</VisuallyHidden>
						{/* TODO: DS loading state */}
						<Loader size={44} mx="auto" ref={loadingRef} mt={24} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						data-qa="stories-and-updates-load-more-button"
						data-testid="stories-and-updates-load-more-button"
						className="max-md:!w-full"
						size="small"
						variant="secondary"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: petitionUpdates.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.local.petition_updates.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
